<template>
  <transition
    name="v-toast-fade"
    @after-leave="handleAfterLeave"
  >
    <div
      v-show="visible"
      :class="[
        'v-toast-wrap',
        ...classNames,
        platformTheme,
        {
          'reg-success': isRegSuccess
        }
      ]"
      :style="{
        top: top ? `${top}px` : null
      }"
    >
      <span
        v-if="dangerouslyUseHTMLString"
        class="v-toast"
        v-html="message"
      />
      <span
        v-else
        class="v-toast"
        :class="[
          type,
          {
            'reg-success': isRegSuccess
          }
        ]"
      >
        <img
          v-if="isRegSuccess"
          class="icon-success"
          src="./images/reg-success@2x.png"
        />
        <img
          v-else-if="type === 'info'"
          class="icon"
          src="./images/info.png"
        />
        <img
          v-else-if="type === 'error'"
          class="icon"
          src="./images/error.png"
        />
        <img
          v-else-if="type === 'success'"
          class="icon"
          src="./images/success.png"
        />
        {{ message }}
        <img
          v-if="closeable"
          src="./images/close@2x.png"
          alt="close"
          class="icon-close"
          @click="close"
        />
      </span>
    </div>
  </transition>
</template>

<script>
import { computed, unref } from '@vue/composition-api'
import { useWindowSize } from '@vueuse/core'
import { defaultToastConfig } from './config'

export default {
  name: 'VToast',
  setup() {
    const { width } = useWindowSize()
    const isDesktop = computed(() => unref(width) >= 750)
    const defaultConfig = computed(() => {
      return isDesktop.value ? defaultToastConfig.desktop : defaultToastConfig.wap
    })

    return {
      isDesktop,
      defaultConfig
    }
  },
  data() {
    return {
      type: '',
      visible: false,
      theme: '',
      message: '',
      duration: defaultToastConfig.wap,
      timer: null,
      closed: false,
      classNames: [],
      onClose: null,
      closeable: false,
      dangerouslyUseHTMLString: false,
      // 距离顶部高度，默认空，上线居中
      top: null
    }
  },
  computed: {
    isRegSuccess() {
      return this.type === 'regSuceess'
    },
    platformTheme() {
      return this.theme || this.defaultConfig.theme
    }
  },
  mounted() {
    this.startTimer()
  },
  methods: {
    handleAfterLeave() {
      this.$destroy(true)
      this.$el.parentNode?.removeChild(this.$el)
    },
    close() {
      if (this.timer) clearTimeout(this.timer)
      this.closed = true
      this.visible = false
    },
    startTimer() {
      const duration = this.duration || this.defaultConfig.duration
      this.timer = setTimeout(() => {
        if (!this.closed) {
          this.close()
        }
        this.onClose && this.onClose()
      }, duration)
    }
  }
}
</script>

<style lang="less">
@disYWap: -30px;
@disYDesktop: -50px;

.v-toast-wrap {
  position: fixed;
  z-index: 100000;
  top: 50%;
  right: 0;
  left: 0;
  height: 0;
  text-align: center;
  transform: translateY(-50%);
  transition:
    opacity 0.3s,
    transform 0.4s,
    top 0.4s;
}

.v-toast {
  position: relative;
  display: inline-block;
  box-sizing: border-box;
  text-align: center;
  transform: translateY(-50%);
}

.v-toast-fade-enter,
.v-toast-fade-leave-active {
  opacity: 0;
}

// wap
@media (max-width: 749px) {
  [data-mode='light'] {
    .v-toast-wrap {
      .v-toast {
        background: fade(#333, 80%);
        color: #fff;
      }
    }
  }

  [data-bg='light'] {
    .v-toast-wrap {
      .v-toast {
        background: fade(#333, 80%);
        color: #fff;
      }
    }
  }

  .v-toast-wrap {
    &.light {
      .v-toast {
        background: rgba(248, 248, 248, 90%);
        color: #333;
      }
    }

    &.dark {
      .v-toast {
        background: fade(#333, 80%);
        color: #fff;
      }
    }
  }

  .v-toast {
    max-width: 70%;
    padding: 20px;
    border-radius: 8px;
    background: rgba(248, 248, 248, 90%);
    color: #333;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;

    &.reg-success {
      display: flex;
      width: 214px;
      height: 40px;
      align-items: center;
      padding: 0;
      border-radius: 20px;
      margin: 0 auto;
      background: #fff;
      box-shadow: 0 0 10px 0 rgba(0, 0, 0, 30%);
      color: #333;
      font-size: 14px;
      font-style: italic;
      font-weight: 500;
      line-height: 40px;
      text-align: center;

      .icon-success {
        width: 32px;
        height: 32px;
        margin: 0 8.5px 0 4px;
      }
    }

    .icon-close {
      position: absolute;
      top: 50%;
      right: 8px;
      width: 8px;
      height: 8px;
      transform: translateY(-50%);
    }
  }

  .v-toast-fade-enter,
  .v-toast-fade-leave-active {
    transform: translateY(@disYWap);
  }
}

// desktop
@media (min-width: 750px) {
  .v-toast-wrap {
    &.light {
      .v-toast {
        background: rgba(248, 248, 248, 80%);
        color: #000;
      }
    }

    &.reg-success {
      .v-toast {
        height: 64px;
        padding: 0 12px 0 60px;
        border-radius: 32px;
        margin: 0 auto;
        background: #fff;
        box-shadow: 0 0 10px 0 rgba(0, 0, 0, 30%);
        color: #333;
        font-size: 16px;
        font-style: italic;
        font-weight: 700;
        line-height: 64px;
      }

      .icon-success {
        position: absolute;
        top: 12px;
        left: 12px;
        width: 40px;
        height: 40px;
      }
    }
  }

  .v-toast {
    top: @disYDesktop;
    max-width: 400px;
    padding: 39px 45px 38px;
    border-radius: 20px;
    background: fade(#333, 80%);
    box-shadow:
      0 3px 6px -4px rgba(0, 0, 0, 12%),
      0 6px 16px 0 rgba(0, 0, 0, 8%),
      0 9px 28px 8px rgba(0, 0, 0, 5%);
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    text-align: left;

    &.info,
    &.success,
    &.error {
      max-width: 600px;
      padding-left: 88px;
      text-align: left;
    }

    .icon {
      position: absolute;
      top: 39px;
      left: 54px;
      width: 24px;
      height: 24px;
      margin: -2px 10px 0 0;
    }

    .icon-close {
      position: absolute;
      top: 50%;
      right: 8px;
      width: 8px;
      height: 8px;
      transform: translateY(-50%);
    }
  }

  .v-toast-fade-enter,
  .v-toast-fade-leave-active {
    transform: translateY(@disYDesktop);
  }
}
</style>
